'use client';

import { Tooltip, Typography } from '@mui/material';
import { FC, useState, useEffect } from 'react';
import { ContentBox, IconBox } from './styled';
import { COLORS } from '@/constants';
import { MessagesIcon, Loader } from '@/components';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { useAuth } from '@/contexts';
import { useGetBrokerRequests } from '@/queris';

type BrokerRequestTooltipProps = {
  objectId?: string;
  hasInfoRequest?: boolean;
};

export const BrokerRequestTooltip: FC<BrokerRequestTooltipProps> = ({
  objectId,
  hasInfoRequest,
}) => {
  const t = useTranslations();
  const [openByClick, setOpenByClick] = useState(false);
  const [openByHover, setOpenByHover] = useState(false);
  const [isMobileOrTouch, setIsMobileOrTouch] = useState(false);

  useEffect(() => {
    const m = window.matchMedia('(hover: none)');
    const set = () => setIsMobileOrTouch(m.matches);
    set();
    m.addEventListener('change', set);
    return () => m.removeEventListener('change', set);
  }, []);

  const isOpen = isMobileOrTouch ? openByClick : openByClick || openByHover;

  const { isAuthenticated } = useAuth();

  const { data, isLoading, isFetching, refetch } = useGetBrokerRequests(objectId, false);

  const handleOpen = () => {
    if (!isFetching) void refetch();
  };

  const handleTriggerClick = () => {
    setOpenByClick((prev) => {
      if (prev) return false;
      handleOpen();
      return true;
    });
  };

  useEffect(() => {
    if (isOpen) handleOpen();
  }, [isOpen]);

  const lastRequest = data?.[0];
  const isCommentExist = lastRequest?.comment !== '';

  if (!hasInfoRequest || !isAuthenticated) return null;

  return (
    <Tooltip
      open={isOpen}
      TransitionProps={{ timeout: 0 }}
      onOpen={handleOpen}
      title={
        <ContentBox onClick={(e) => e.stopPropagation()}>
          {isLoading || isFetching ? (
            <Loader isSmall />
          ) : (
            <>
              <Typography
                fontSize={14}
                fontWeight={400}
                lineHeight="140%"
                color={COLORS['neutral/600']}
              >
                {dayjs(lastRequest?.createdAt).format('DD.MM.YYYY HH:mm')}
              </Typography>
              <Typography
                fontSize={14}
                fontWeight={500}
                lineHeight="150%"
                color={COLORS['black']}
                sx={{
                  opacity: isCommentExist ? 1 : 0.4,
                }}
              >
                {isCommentExist ? lastRequest?.comment : t('noComments')}
              </Typography>
            </>
          )}
        </ContentBox>
      }
      placement="bottom-start"
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: COLORS['neutral/800'],
            fontSize: '12px',
            lineHeight: 1,
            padding: '10px 12px',
            borderRadius: '6px',
            boxShadow: '0px 2px 10px 0px #0000001A',
          },
        },
      }}
    >
      <IconBox
        onClick={handleTriggerClick}
        onMouseEnter={!isMobileOrTouch ? () => setOpenByHover(true) : undefined}
        onMouseLeave={!isMobileOrTouch ? () => setOpenByHover(false) : undefined}
        as="button"
        type="button"
        role="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <MessagesIcon />
        <Typography fontSize={14} fontWeight={700} lineHeight="16px" color={COLORS['primary/500']}>
          {t('contacted')}
        </Typography>
      </IconBox>
    </Tooltip>
  );
};
